import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
// ─────────────────────────────────────────────────────────────────────────────
// 18. REPORTS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const ReportsPage = ({ companyId }) => {
  const [activeReport, setActiveReport] = useState('stock');
  const [data, setData]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const { show, ToastEl }               = useToast();

  const reportTypes = [
    { id:'stock',     label:'Stock Report',       Icon:Package },
    { id:'movements', label:'Movement Report',    Icon:ArrowLeftRight },
    { id:'purchase',  label:'Purchase Report',    Icon:ShoppingCart },
    { id:'valuation', label:'Valuation Report',   Icon:Receipt },
  ];
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  const fetchReport = async () => {
    setLoading(true);
    const filters = { companyId, startDate, endDate };
    let res;
    if (activeReport==='stock')     res = await reportController.getStockReport(filters);
    else if (activeReport==='movements') res = await reportController.getMovementReport(filters);
    else if (activeReport==='purchase')  res = await reportController.getPurchaseReport(filters);
    else res = await reportController.getValuationReport(filters);
    if (res.success) setData(res.data);
    else show(res.message, 'error');
    setLoading(false);
  };

  useEffect(() => { fetchReport(); }, [activeReport, companyId]);

  const handleExport = async () => {
    const res = await reportController.export(activeReport, { companyId, startDate, endDate });
    if (res.success) {
      const url = URL.createObjectURL(new Blob([res.data],{type:'text/csv'}));
      const a = document.createElement('a'); a.href=url; a.download=`inventory-${activeReport}-report.csv`; a.click();
      URL.revokeObjectURL(url);
    } else show(res.message,'error');
  };

  return (
    <div style={S.page}>
      {ToastEl}
      <PageHeader title="Inventory Reports" subtitle="Analytics and insights" Icon={FileText} color={C.primary}
        action={<button style={S.btnSec} onClick={handleExport}><Download size={15}/> Export CSV</button>} />

      {/* Report tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {reportTypes.map(r=>(
          <button key={r.id} style={{ ...S.btnSec, ...(activeReport===r.id && { background:C.primary, color:'#fff', borderColor:C.primary }) }} onClick={()=>setActiveReport(r.id)}>
            <r.Icon size={14}/> {r.label}
          </button>
        ))}
      </div>

      {/* Date filter */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <input type="date" style={{ ...S.input, width:'auto' }} value={startDate} onChange={e=>setStartDate(e.target.value)} />
        <span style={{ color:C.textSoft }}>to</span>
        <input type="date" style={{ ...S.input, width:'auto' }} value={endDate} onChange={e=>setEndDate(e.target.value)} />
        <button style={S.btnPri} onClick={fetchReport}>Apply</button>
      </div>

      <div style={S.card}>
        {loading ? <div style={S.center}>Generating report...</div> : (
          <div style={{ overflowX:'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {activeReport==='stock'     && ['Product','SKU','Category','Warehouse','Stock','Min Stock','MRP','Purchase Price','Stock Value'].map(h=><th key={h} style={S.th}>{h}</th>)}
                  {activeReport==='movements' && ['Product','SKU','Type','Qty','Date','Warehouse','Reference','Notes'].map(h=><th key={h} style={S.th}>{h}</th>)}
                  {activeReport==='purchase'  && ['PO No.','Supplier','Date','Items','Amount','Paid','Balance','Status'].map(h=><th key={h} style={S.th}>{h}</th>)}
                  {activeReport==='valuation' && ['Product','SKU','Category','Stock','Purchase Price','MRP','Purchase Value','MRP Value','Margin'].map(h=><th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.map((row,i)=>(
                  <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#f9fafb'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    {activeReport==='stock' && <>
                      <td style={S.td}><div style={{ fontWeight:700 }}>{row.name}</div></td>
                      <td style={S.td}><span style={{ fontFamily:'monospace', fontSize:12 }}>{row.sku||'—'}</span></td>
                      <td style={S.td}>{row.categoryName||'—'}</td>
                      <td style={S.td}>{row.warehouseName||'—'}</td>
                      <td style={S.td}><span style={{ fontWeight:700, color: row.currentStock<=row.minStock?C.danger:C.success }}>{row.currentStock}</span></td>
                      <td style={S.td}>{row.minStock}</td>
                      <td style={S.td}>{money(row.mrp)}</td>
                      <td style={S.td}>{money(row.purchasePrice)}</td>
                      <td style={S.td}><span style={{ fontWeight:700 }}>{money((row.currentStock||0)*(row.purchasePrice||0))}</span></td>
                    </>}
                    {activeReport==='valuation' && <>
                      <td style={S.td}><div style={{ fontWeight:700 }}>{row.name}</div></td>
                      <td style={S.td}><span style={{ fontFamily:'monospace', fontSize:12 }}>{row.sku||'—'}</span></td>
                      <td style={S.td}>{row.categoryName||'—'}</td>
                      <td style={S.td}>{row.currentStock}</td>
                      <td style={S.td}>{money(row.purchasePrice)}</td>
                      <td style={S.td}>{money(row.mrp)}</td>
                      <td style={S.td}>{money((row.currentStock||0)*(row.purchasePrice||0))}</td>
                      <td style={S.td}>{money((row.currentStock||0)*(row.mrp||0))}</td>
                      <td style={S.td}><span style={{ color:C.success, fontWeight:700 }}>{row.mrp&&row.purchasePrice ? `${(((row.mrp-row.purchasePrice)/row.purchasePrice)*100).toFixed(1)}%` : '—'}</span></td>
                    </>}
                    {(activeReport==='movements'||activeReport==='purchase') && Object.values(row).slice(0,8).map((v,vi)=><td key={vi} style={S.td}>{typeof v==='number'&&vi>1?money(v):v||'—'}</td>)}
                  </tr>
                ))}
                {!data.length && <tr><td colSpan={9}><div style={S.center}>No data available for selected criteria</div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
export default ReportsPage;